/**
 * Unified Execution Page
 *
 * Main page for executing agents with the unified architecture.
 * Supports manual agent selection and AI-orchestrated execution.
 */
import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CpuChipIcon,
  SparklesIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SignalIcon,
  SignalSlashIcon,
  StopIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import apiClient from '@/services/api'
import { useIntelligentExecutionWS } from '@/hooks/useIntelligentExecutionWS'
import {
  ProviderModeSelector,
  ExecutionModeSelector,
  UnifiedAgentSelector,
} from '@/components/unified'
import ExecutionTimeline from '@/components/intelligent/ExecutionTimeline'
import ZiroGraphDiagram from '@/components/intelligent/ZiroGraphDiagram'
import {
  UnifiedAgent,
  AgentProviderMode,
  AgentExecutionMode,
  ProviderStatus,
  UnifiedExecuteResponse,
  ExecutionOptions,
} from '@/types/unified'

type ExecutionType = 'manual' | 'ai_orchestrated'
type ViewMode = 'config' | 'agents' | 'results'

const UnifiedExecutionPage = observer(() => {
  // Execution type: manual selection vs AI orchestration
  const [executionType, setExecutionType] = useState<ExecutionType>('manual')

  // Current view mode
  const [viewMode, setViewMode] = useState<ViewMode>('config')

  // Prompt input
  const [prompt, setPrompt] = useState('')

  // Selected agents (for manual mode)
  const [agents, setAgents] = useState<UnifiedAgent[]>([])
  const [selectedAgent, setSelectedAgent] = useState<UnifiedAgent | null>(null)
  const [loadingAgents, setLoadingAgents] = useState(true)

  // Execution options
  const [options, setOptions] = useState<ExecutionOptions>({
    provider_mode: AgentProviderMode.AUTO,
    execution_mode: AgentExecutionMode.TRIAD,
    quality_threshold: 7.0,
    max_revisions: 2,
    temperature: 0.3,
  })

  // Show advanced options
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Provider status
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null)

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false)
  const [executionResult, setExecutionResult] = useState<UnifiedExecuteResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Visualization mode
  const [visualMode, setVisualMode] = useState<'timeline' | 'graph'>('graph')

  // WebSocket for real-time updates
  const {
    isConnected,
    connect,
    disconnect,
    subscribeToExecution,
    steps,
    currentPhase,
    error: wsError,
    reset: resetWS,
  } = useIntelligentExecutionWS({
    userId: 'demo-user',
    token: 'demo-token',
    onEvent: (event) => {
      console.log('Unified execution event:', event)
    },
  })

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingAgents(true)
        const [agentsData, providersData] = await Promise.all([
          apiClient.getUnifiedAgents(),
          apiClient.getUnifiedProviders(),
        ])
        setAgents(agentsData.agents || [])
        setProviderStatus(providersData)
      } catch (err) {
        console.error('Failed to load unified data:', err)
        setError('Failed to load agents and providers')
      } finally {
        setLoadingAgents(false)
      }
    }
    loadData()
  }, [])

  // Connect to WebSocket on mount
  useEffect(() => {
    connect()
    return () => disconnect()
  }, [connect, disconnect])

  const handleExecute = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt')
      return
    }

    if (executionType === 'manual' && !selectedAgent) {
      setError('Please select an agent')
      return
    }

    setIsExecuting(true)
    setError(null)
    setExecutionResult(null)
    resetWS()
    setViewMode('results')

    try {
      const request = {
        prompt,
        agent_id: executionType === 'manual' ? selectedAgent?.id : undefined,
        options: {
          ...options,
          use_ai_orchestration: executionType === 'ai_orchestrated',
        },
      }

      const result = await apiClient.executeUnified(request)

      // Subscribe to WebSocket for this execution
      if (result.execution_id) {
        subscribeToExecution(result.execution_id)
      }

      setExecutionResult(result)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Execution failed')
    } finally {
      setIsExecuting(false)
    }
  }

  const handleStop = () => {
    // TODO: Implement stop execution API call
    setIsExecuting(false)
  }

  const getQualityColor = (score: number) => {
    if (score >= 8) return 'text-green-400'
    if (score >= 6) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-400" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-400" />
      case 'running':
        return <ArrowPathIcon className="w-5 h-5 text-blue-400 animate-spin" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center">
            <CpuChipIcon className="w-8 h-8 mr-3 text-primary-400" />
            Unified Execution
          </h1>
          <p className="text-muted-foreground mt-1">
            Execute agents with dual-mode providers and configurable execution pipelines
          </p>
        </div>

        {/* Connection Status */}
        <div className="flex items-center gap-3">
          {isConnected ? (
            <span className="flex items-center text-green-400 text-sm">
              <SignalIcon className="w-4 h-4 mr-1" />
              Live
            </span>
          ) : (
            <span className="flex items-center text-muted-foreground text-sm">
              <SignalSlashIcon className="w-4 h-4 mr-1" />
              Disconnected
            </span>
          )}
        </div>
      </div>

      {/* Provider Status */}
      {providerStatus && (
        <div className="bg-card rounded-lg p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Available Providers</h3>
          <div className="flex flex-wrap gap-3">
            <div
              className={clsx(
                'flex items-center px-3 py-1.5 rounded-full text-sm',
                providerStatus.local ? 'bg-green-500/15 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'
              )}
            >
              {providerStatus.local ? (
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
              ) : (
                <XCircleIcon className="w-4 h-4 mr-1.5" />
              )}
              Local (Ollama)
            </div>
            <div
              className={clsx(
                'flex items-center px-3 py-1.5 rounded-full text-sm',
                providerStatus.anthropic ? 'bg-blue-500/15 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-muted text-muted-foreground'
              )}
            >
              {providerStatus.anthropic ? (
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
              ) : (
                <XCircleIcon className="w-4 h-4 mr-1.5" />
              )}
              Anthropic
            </div>
            <div
              className={clsx(
                'flex items-center px-3 py-1.5 rounded-full text-sm',
                providerStatus.openai ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-muted text-muted-foreground'
              )}
            >
              {providerStatus.openai ? (
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
              ) : (
                <XCircleIcon className="w-4 h-4 mr-1.5" />
              )}
              OpenAI
            </div>
            <div
              className={clsx(
                'flex items-center px-3 py-1.5 rounded-full text-sm',
                providerStatus.google ? 'bg-orange-500/15 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-muted text-muted-foreground'
              )}
            >
              {providerStatus.google ? (
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
              ) : (
                <XCircleIcon className="w-4 h-4 mr-1.5" />
              )}
              Google
            </div>
          </div>
        </div>
      )}

      {/* Execution Type Toggle */}
      <div className="bg-card rounded-lg p-4">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Execution Mode</h3>
        <div className="flex gap-3">
          <button
            onClick={() => setExecutionType('manual')}
            className={clsx(
              'flex-1 flex items-center justify-center p-4 rounded-lg border-2 transition-all',
              executionType === 'manual'
                ? 'bg-primary-900/30 border-primary-600'
                : 'bg-background border-border hover:border-border'
            )}
          >
            <CpuChipIcon className={clsx('w-6 h-6 mr-3', executionType === 'manual' ? 'text-primary-400' : 'text-muted-foreground')} />
            <div className="text-left">
              <h4 className={clsx('font-medium', executionType === 'manual' ? 'text-white' : 'text-muted-foreground')}>
                Manual Selection
              </h4>
              <p className="text-xs text-muted-foreground">Choose a specific agent to execute</p>
            </div>
          </button>

          <button
            onClick={() => setExecutionType('ai_orchestrated')}
            className={clsx(
              'flex-1 flex items-center justify-center p-4 rounded-lg border-2 transition-all',
              executionType === 'ai_orchestrated'
                ? 'bg-purple-900/30 border-purple-600'
                : 'bg-background border-border hover:border-border'
            )}
          >
            <SparklesIcon className={clsx('w-6 h-6 mr-3', executionType === 'ai_orchestrated' ? 'text-purple-400' : 'text-muted-foreground')} />
            <div className="text-left">
              <h4 className={clsx('font-medium', executionType === 'ai_orchestrated' ? 'text-white' : 'text-muted-foreground')}>
                AI Orchestrated
              </h4>
              <p className="text-xs text-muted-foreground">Let AI select and coordinate agents</p>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content - Tab-based */}
      <div className="bg-card rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setViewMode('config')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium transition-colors',
              viewMode === 'config'
                ? 'text-primary-400 border-b-2 border-primary-400 bg-muted'
                : 'text-muted-foreground hover:text-white'
            )}
          >
            <Cog6ToothIcon className="w-4 h-4 inline mr-2" />
            Configuration
          </button>
          {executionType === 'manual' && (
            <button
              onClick={() => setViewMode('agents')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                viewMode === 'agents'
                  ? 'text-primary-400 border-b-2 border-primary-400 bg-muted'
                  : 'text-muted-foreground hover:text-white'
              )}
            >
              <CpuChipIcon className="w-4 h-4 inline mr-2" />
              Agents {selectedAgent && `(${selectedAgent.name})`}
            </button>
          )}
          <button
            onClick={() => setViewMode('results')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium transition-colors',
              viewMode === 'results'
                ? 'text-primary-400 border-b-2 border-primary-400 bg-muted'
                : 'text-muted-foreground hover:text-white'
            )}
          >
            <PlayIcon className="w-4 h-4 inline mr-2" />
            Results
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Configuration Tab */}
          {viewMode === 'config' && (
            <div className="space-y-6">
              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Task Description
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe what you want to accomplish..."
                  className="w-full h-32 px-4 py-3 bg-background border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  disabled={isExecuting}
                />
              </div>

              {/* Mode Selectors */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProviderModeSelector
                  value={options.provider_mode || AgentProviderMode.AUTO}
                  onChange={(mode) => setOptions({ ...options, provider_mode: mode })}
                  providerStatus={providerStatus || undefined}
                  disabled={isExecuting}
                />

                <ExecutionModeSelector
                  value={options.execution_mode || AgentExecutionMode.TRIAD}
                  onChange={(mode) => setOptions({ ...options, execution_mode: mode })}
                  disabled={isExecuting}
                />
              </div>

              {/* Advanced Options */}
              <div>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center text-sm text-muted-foreground hover:text-white"
                >
                  {showAdvanced ? (
                    <ChevronUpIcon className="w-4 h-4 mr-1" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 mr-1" />
                  )}
                  Advanced Options
                </button>

                {showAdvanced && (
                  <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-background rounded-lg">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">
                        Quality Threshold (1-10)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        step="0.5"
                        value={options.quality_threshold}
                        onChange={(e) =>
                          setOptions({ ...options, quality_threshold: parseFloat(e.target.value) })
                        }
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isExecuting}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Max Revisions</label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={options.max_revisions}
                        onChange={(e) =>
                          setOptions({ ...options, max_revisions: parseInt(e.target.value) })
                        }
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isExecuting}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Temperature</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={options.temperature}
                        onChange={(e) =>
                          setOptions({ ...options, temperature: parseFloat(e.target.value) })
                        }
                        className="w-full px-3 py-2 bg-card border border-border rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isExecuting}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Execute Button */}
              <div className="flex gap-3">
                {isExecuting ? (
                  <button
                    onClick={handleStop}
                    className="flex items-center px-6 py-3 rounded-lg font-medium bg-red-600 hover:bg-red-500 text-white transition-colors"
                  >
                    <StopIcon className="w-5 h-5 mr-2" />
                    Stop
                  </button>
                ) : (
                  <button
                    onClick={handleExecute}
                    disabled={!prompt.trim() || (executionType === 'manual' && !selectedAgent)}
                    className={clsx(
                      'flex items-center px-6 py-3 rounded-lg font-medium transition-colors',
                      'bg-primary-600 hover:bg-primary-500 text-white',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    <PlayIcon className="w-5 h-5 mr-2" />
                    Execute
                  </button>
                )}

                {executionType === 'manual' && !selectedAgent && (
                  <button
                    onClick={() => setViewMode('agents')}
                    className="flex items-center px-4 py-3 rounded-lg font-medium bg-muted hover:bg-accent text-white transition-colors"
                  >
                    <CpuChipIcon className="w-5 h-5 mr-2" />
                    Select Agent
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Agents Tab */}
          {viewMode === 'agents' && executionType === 'manual' && (
            <UnifiedAgentSelector
              agents={agents}
              selectedAgentId={selectedAgent?.id}
              onSelect={(agent) => {
                setSelectedAgent(agent)
                // Update options from agent defaults
                setOptions({
                  ...options,
                  provider_mode: agent.provider_mode as typeof options.provider_mode,
                  execution_mode: agent.execution_mode as typeof options.execution_mode,
                  quality_threshold: agent.quality_threshold,
                  max_revisions: agent.max_revisions,
                })
              }}
              loading={loadingAgents}
            />
          )}

          {/* Results Tab */}
          {viewMode === 'results' && (
            <div className="space-y-6">
              {/* Error Display */}
              {(error || wsError) && (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-400">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                    <p className="font-medium">Error</p>
                  </div>
                  <p className="text-sm mt-1">{error || wsError}</p>
                </div>
              )}

              {/* Visualization Mode Toggle */}
              <div className="flex justify-end">
                <div className="inline-flex rounded-lg bg-background p-1">
                  <button
                    onClick={() => setVisualMode('graph')}
                    className={clsx(
                      'px-3 py-1.5 text-sm rounded-md transition-colors',
                      visualMode === 'graph'
                        ? 'bg-primary-600 text-white'
                        : 'text-muted-foreground hover:text-white'
                    )}
                  >
                    Graph
                  </button>
                  <button
                    onClick={() => setVisualMode('timeline')}
                    className={clsx(
                      'px-3 py-1.5 text-sm rounded-md transition-colors',
                      visualMode === 'timeline'
                        ? 'bg-primary-600 text-white'
                        : 'text-muted-foreground hover:text-white'
                    )}
                  >
                    Timeline
                  </button>
                </div>
              </div>

              {/* Visualization */}
              {(isExecuting || steps.length > 0) && (
                <>
                  {visualMode === 'graph' ? (
                    <ZiroGraphDiagram currentPhase={currentPhase} showFullGraph={true} />
                  ) : (
                    <div className="bg-background rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <ClockIcon className="w-5 h-5 mr-2 text-primary-400" />
                        Execution Timeline
                        {isExecuting && (
                          <span className="ml-2 flex items-center text-sm text-blue-400">
                            <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                            Live
                          </span>
                        )}
                      </h3>
                      <ExecutionTimeline steps={steps} currentPhase={currentPhase} autoScroll={true} />
                    </div>
                  )}
                </>
              )}

              {/* Execution Result */}
              {executionResult && (
                <div className="bg-background rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center">
                      {getStatusIcon(executionResult.status)}
                      <span className="ml-2">Execution {executionResult.status}</span>
                    </h3>
                    <span className="text-xs text-muted-foreground">ID: {executionResult.execution_id}</span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-card rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Provider</p>
                      <p className="text-lg font-bold text-white">{executionResult.provider_used || 'N/A'}</p>
                    </div>
                    <div className="bg-card rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Quality Score</p>
                      <p
                        className={clsx(
                          'text-lg font-bold',
                          getQualityColor(executionResult.quality_score || 0)
                        )}
                      >
                        {executionResult.quality_score?.toFixed(1) || 'N/A'}/10
                      </p>
                    </div>
                    <div className="bg-card rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Revisions</p>
                      <p className="text-lg font-bold text-white">{executionResult.revision_count || 0}</p>
                    </div>
                    <div className="bg-card rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">Tokens</p>
                      <p className="text-lg font-bold text-white">{executionResult.tokens_used || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Task Results */}
                  {executionResult.task_results && (
                    Array.isArray(executionResult.task_results)
                      ? executionResult.task_results.length > 0
                      : Object.keys(executionResult.task_results).length > 0
                  ) && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Task Results</h4>
                      <div className="space-y-2">
                        {(Array.isArray(executionResult.task_results)
                          ? executionResult.task_results
                          : Object.values(executionResult.task_results)
                        ).map((task: any, idx: number) => (
                          <div
                            key={idx}
                            className="bg-card rounded-lg p-3 border border-border"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                {getStatusIcon(task.status)}
                                <span className="ml-2 text-white">{task.specialist_type || task.specialist}</span>
                              </div>
                              <div className="flex items-center gap-3 text-sm">
                                {task.quality_score && (
                                  <span className={getQualityColor(task.quality_score)}>
                                    Quality: {task.quality_score}/10
                                  </span>
                                )}
                                {task.revision_count > 0 && (
                                  <span className="text-yellow-400">
                                    <ArrowPathIcon className="w-4 h-4 inline mr-1" />
                                    {task.revision_count} revision{task.revision_count > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Final Output */}
                  {executionResult.output && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Output</h4>
                      <div className="bg-card rounded-lg p-4 border border-border max-h-96 overflow-y-auto">
                        <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-mono">
                          {executionResult.output}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Empty state */}
              {!isExecuting && !executionResult && !error && (
                <div className="text-center py-12 text-muted-foreground">
                  <PlayIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No execution results yet</p>
                  <p className="text-sm mt-1">Configure and run an execution to see results here</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Agent Preview (for manual mode) */}
      {executionType === 'manual' && selectedAgent && viewMode !== 'agents' && (
        <div className="bg-card rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-primary-900/50 rounded-lg mr-3">
                {selectedAgent.is_ziro_specialist ? (
                  <SparklesIcon className="w-5 h-5 text-primary-400" />
                ) : (
                  <CpuChipIcon className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <h4 className="font-medium text-white">{selectedAgent.name}</h4>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{selectedAgent.provider_mode}</span>
                  <span>•</span>
                  <span>{selectedAgent.execution_mode}</span>
                  {selectedAgent.specialist_type && (
                    <>
                      <span>•</span>
                      <span className="text-primary-400">{selectedAgent.specialist_type}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setViewMode('agents')}
              className="text-sm text-primary-400 hover:text-primary-300"
            >
              Change
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default UnifiedExecutionPage
